import { useState, useCallback, useEffect } from 'react';
import type { AgentType, ChatMessage } from '../types/chat';

export interface ChatHistory {
  id: string;
  title: string;
  timestamp: number;
  agent: AgentType;
  messages: ChatMessage[];
}

const STORAGE_KEY_PREFIX = 'ellis-chat-history';

const getStorageKey = (userEmail: string): string => {
  return `${STORAGE_KEY_PREFIX}-${userEmail}`;
};

const getInitialChatHistory = (): ChatHistory[] => {
  const now = Date.now();
  return [
    {
      id: '1',
      title: 'CBRE hackathon themes',
      timestamp: now - 86400000 * 5,
      agent: 'jira-agile',
      messages: [
        {
          id: 'msg-1-1',
          role: 'user',
          content: 'What are the key themes for the upcoming CBRE hackathon?',
          agent: 'jira-agile',
          timestamp: now - 86400000 * 5 + 1000,
        },
        {
          id: 'msg-1-2',
          role: 'assistant',
          content:
            'Based on the DevX portal roadmap, the main hackathon themes are:\n\n1. **AI/ML Integration** - Building smarter automation and intelligent features\n2. **Developer Experience** - Tools and workflows to enhance productivity\n3. **Cloud Optimization** - Improving performance and cost efficiency\n4. **Security & Compliance** - Strengthening data protection and regulatory adherence\n\nThese align with our 2026 strategic initiatives. Would you like me to pull specific JIRA tickets related to any of these themes?',
          agent: 'jira-agile',
          timestamp: now - 86400000 * 5 + 2000,
        },
        {
          id: 'msg-1-3',
          role: 'user',
          content: 'Can you list the open stories for the AI/ML theme?',
          agent: 'jira-agile',
          timestamp: now - 86400000 * 5 + 3000,
        },
        {
          id: 'msg-1-4',
          role: 'assistant',
          content:
            'Here are the open JIRA stories for AI/ML Integration:\n\n• **CBRE-2401**: Implement LLM-powered chat interface\n• **CBRE-2415**: Build ML model for demand forecasting\n• **CBRE-2423**: Create intelligent notification system\n• **CBRE-2445**: Develop predictive maintenance alerts\n\nTotal: 4 stories | Sprint: Q2 2026 | Team: Platform Engineering\n\nWould you like to see details on any specific story?',
          agent: 'jira-agile',
          timestamp: now - 86400000 * 5 + 4000,
        },
      ],
    },
    {
      id: '2',
      title: '2026 Roadmap Executive Summary',
      timestamp: now - 86400000 * 4,
      agent: 'ado-agile',
      messages: [
        {
          id: 'msg-2-1',
          role: 'user',
          content:
            'Summarize the 2026 roadmap priorities for our executive team',
          agent: 'ado-agile',
          timestamp: now - 86400000 * 4 + 1000,
        },
        {
          id: 'msg-2-2',
          role: 'assistant',
          content:
            '**2026 Strategic Roadmap Summary**\n\n**Q1-Q2 Focus:**\n- Launch DevX Portal 2.0 with enhanced AI capabilities\n- Migrate 80% of legacy services to microservices architecture\n- Implement comprehensive cloud cost optimization\n- Release mobile application for field teams\n\n**Q3-Q4 Focus:**\n- Establish security and compliance framework\n- Build advanced analytics and reporting dashboard\n- Scale platform to support 50+ enterprise clients\n- Develop industry-specific solutions\n\n**Key Metrics:**\n- Developer productivity: +45% improvement\n- Time-to-market: Reduce by 30%\n- System reliability: 99.95% uptime SLA\n- Cost efficiency: 25% reduction in cloud spend\n\nEstimated investment: $2.4M | Expected ROI: 350%\n\nWould you like a deeper dive into any specific quarter?',
          agent: 'ado-agile',
          timestamp: now - 86400000 * 4 + 2000,
        },
        {
          id: 'msg-2-3',
          role: 'user',
          content: 'What are the risks and mitigation strategies?',
          agent: 'ado-agile',
          timestamp: now - 86400000 * 4 + 3000,
        },
        {
          id: 'msg-2-4',
          role: 'assistant',
          content:
            '**Risk Assessment & Mitigation**\n\n**High Priority Risks:**\n\n1. **Resource Constraints** (High Impact)\n   - Mitigation: Hire 5 senior engineers by Q1, establish partnerships\n\n2. **Technology Integration** (Medium Impact)\n   - Mitigation: Parallel testing environment, phased rollout approach\n\n3. **Market Competition** (Medium Impact)\n   - Mitigation: Accelerate feature delivery, focus on differentiation\n\n4. **Data Migration** (High Impact)\n   - Mitigation: Comprehensive audit, backup systems, rollback plans\n\n**Timeline Buffers:**\n- 15% slack built into each quarter\n- Monthly risk review meetings\n- Executive steering committee oversight\n\nStatus: On Track ✓\nNext review: May 15, 2026',
          agent: 'ado-agile',
          timestamp: now - 86400000 * 4 + 4000,
        },
      ],
    },
  ];
};

const loadFromLocalStorage = (userEmail: string): ChatHistory[] => {
  if (!userEmail) {
    return getInitialChatHistory();
  }

  try {
    const key = getStorageKey(userEmail);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load chat history from localStorage:', error);
  }

  return getInitialChatHistory();
};

const saveToLocalStorage = (
  userEmail: string,
  history: ChatHistory[],
): void => {
  if (!userEmail) return;

  try {
    const key = getStorageKey(userEmail);
    localStorage.setItem(key, JSON.stringify(history));
  } catch (error) {
    console.error('Failed to save chat history to localStorage:', error);
  }
};

export function useChatHistory(userEmail?: string) {
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>(() =>
    loadFromLocalStorage(userEmail || ''),
  );
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);

  // When userEmail becomes available or changes, reload from localStorage
  useEffect(() => {
    if (userEmail) {
      const stored = loadFromLocalStorage(userEmail);
      setChatHistory(stored);
    }
  }, [userEmail]);

  // Save to localStorage whenever chat history changes
  useEffect(() => {
    if (userEmail) {
      saveToLocalStorage(userEmail, chatHistory);
    }
  }, [chatHistory, userEmail]);

  const addToHistory = useCallback(
    (title: string, agent: AgentType, messages: ChatMessage[] = []): string => {
      const newId = Date.now().toString();
      setChatHistory(prev => [
        {
          id: newId,
          title,
          timestamp: Date.now(),
          agent,
          messages,
        },
        ...prev,
      ]);
      return newId;
    },
    [],
  );

  const loadChatHistory = useCallback(
    (chatId: string): ChatHistory | undefined => {
      let foundChat: ChatHistory | undefined;
      setChatHistory(prev => {
        foundChat = prev.find(c => c.id === chatId);
        return prev;
      });
      if (foundChat) {
        setCurrentChatId(chatId);
      }
      return foundChat;
    },
    [],
  );

  const updateChatMessages = useCallback(
    (chatId: string, messages: ChatMessage[]) => {
      setChatHistory(prev =>
        prev.map(chat => (chat.id === chatId ? { ...chat, messages } : chat)),
      );
    },
    [],
  );

  const clearHistory = useCallback(() => {
    setChatHistory([]);
    if (userEmail) {
      const key = getStorageKey(userEmail);
      localStorage.removeItem(key);
    }
  }, [userEmail]);

  const deleteFromHistory = useCallback(
    (chatId: string) => {
      setChatHistory(prev => prev.filter(chat => chat.id !== chatId));
      if (currentChatId === chatId) {
        setCurrentChatId(null);
      }
    },
    [currentChatId],
  );

  return {
    chatHistory,
    currentChatId,
    setCurrentChatId,
    addToHistory,
    loadChatHistory,
    updateChatMessages,
    clearHistory,
    deleteFromHistory,
  };
}
